// File: src/MathPractice/SinusoidToolPractice.js
import { useState, useRef } from "react";
import './MathPractice.css'
import TrigPracticeGraph from "./TrigPracticeGraph";
import { awardToken } from "../../awardtoken.js"; // adjust path as needed

const DEG_TO_RAD = deg => (deg * Math.PI) / 180;
const TRIG_FUNCS = [
  { name: "sin", label: "Sine" },
  { name: "cos", label: "Cosine" },
  { name: "tan", label: "Tangent" },
];
const ANGLE_POOL = [0, 30, 45, 60, 90, 120, 135, 150, 180, 210, 225, 240, 270, 300, 330, 360];

function randomAngle() {
  return ANGLE_POOL[Math.floor(Math.random() * ANGLE_POOL.length)];
}
function randomTrigFunc() {
  return TRIG_FUNCS[Math.floor(Math.random() * TRIG_FUNCS.length)].name;
}
function generateWorksheetQuestions(n) {
  const used = new Set();
  const qs = [];
  while (qs.length < n) {
    const func = randomTrigFunc();
    const angle = randomAngle();
    const key = `${func}_${angle}`;
    if (!used.has(key)) {
      used.add(key);
      const rad = DEG_TO_RAD(angle);
      let answer;
      if (func === "sin") answer = Math.sin(rad);
      if (func === "cos") answer = Math.cos(rad);
      if (func === "tan") answer = Math.tan(rad);
      qs.push({
        func,
        angle,
        answer,
        label: `${func}(${angle}°) = _________`,
      });
    }
  }
  return qs;
}

export default function SinusoidToolPractice() {
  // Interactive Practice State
  const [func, setFunc] = useState(randomTrigFunc());
  const [angle, setAngle] = useState(randomAngle());
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);


  // Worksheet mode
  const [worksheet, setWorksheet] = useState(null);

  // Score state
  const [correct, setCorrect] = useState(0);
  const [total, setTotal] = useState(0);
  const tokensAwarded = useRef(0);

  // Evaluate answer
  const rad = DEG_TO_RAD(angle);
  let answer;
  if (func === "sin") answer = Math.sin(rad);
  if (func === "cos") answer = Math.cos(rad);
  if (func === "tan") answer = Math.tan(rad);

function checkAnswer() {
  if (waitingForNext) return;
  const user = parseFloat(input);
  const tol = func === "tan" ? 0.1 : 0.02;
  const isOk = !isNaN(user) && Math.abs(user - answer) < tol;
  setTotal(t => t + 1);
  if (isOk) {
    setFeedback("✅ Correct!");
    setCorrect(c => {
      const newScore = c + 1;
      if (Math.floor(newScore / 10) > tokensAwarded.current) {
        awardToken();
        tokensAwarded.current = Math.floor(newScore / 10);
      }
      return newScore;
    });
    setTimeout(() => {
      nextQ();
      setFeedback(null);
      setWaitingForNext(false);
    }, 700);
  } else {
    setFeedback(`❌ The answer is ${answer.toFixed(3)}`);
    setWaitingForNext(true);
  }
}


  function nextQ() {
    setFunc(randomTrigFunc());
    setAngle(randomAngle());
    setInput("");
    setFeedback(null);
  }

  // -- Printable Worksheet Render --
  if (worksheet) {
    return (
      <div className="worksheet">
        <h2 style={{ textAlign: "center", marginBottom: 12 }}>
          Trigonometric Functions Worksheet
        </h2>
        <ol style={{ margin: "0 0 1.5em 2em", fontSize: "1.13em" }}>
          {worksheet.map((q, idx) => (
            <li key={idx} style={{ margin: "1.1em 0" }}>
              <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                {q.func}({q.angle}°) = _________
              </span>
              <div style={{ margin: "0.3em 0" }}>
                <TrigPracticeGraph
                  mode={q.func}
                  highlightX={DEG_TO_RAD(q.angle)}
                  width={110}
                  height={55}
                />
              </div>
            </li>
          ))}
        </ol>
        <button className="no-print math-pill-btn" style={{ marginTop: 12 }} onClick={() => setWorksheet(null)}>
          Back to Practice
        </button>
      </div>
    );
  }

  // -- Interactive Practice Render --
  return (
    <div className="math-practice-card" style={{ maxWidth: 600, margin: "32px auto 0 auto" }}>
      <h2 className="math-practice-title">Trig Graph Practice</h2>
      <div style={{ marginBottom: 12 }}>
        <label>
          Function:&nbsp;
          <select value={func} onChange={e => setFunc(e.target.value)}>
            <option value="sin">sin</option>
            <option value="cos">cos</option>
            <option value="tan">tan</option>
          </select>
        </label>
        <label style={{ marginLeft: 12 }}>
          Angle:&nbsp;
          <input
            type="number"
            min={-360}
            max={360}
            value={angle}
            onChange={e => setAngle(Number(e.target.value))}
            style={{ width: 70 }}
          />
          <span>&deg;</span>
        </label>
        <button className="no-print math-pill-btn" onClick={nextQ} style={{ marginLeft: 14, fontSize: "0.97em" }}>
          Random
        </button>
      </div>
      <div style={{ marginTop: 12, fontSize: "1.23em" }}>
  Calculate <b>{func}({angle}&deg;)</b> ={" "}
  <input
    type="number"
    value={input}
    onChange={e => setInput(e.target.value)}
    className="math-practice-input"
    style={{ width: 110, marginLeft: 2, marginRight: 2, fontSize: "1.07em" }}
    disabled={waitingForNext}
  />
  <button
    className="no-print math-pill-btn"
    onClick={checkAnswer}
    style={{ fontSize: "0.98em", marginLeft: 8 }}
    disabled={waitingForNext}
  >
    Check
  </button>
  {waitingForNext && (
    <button
      className="math-pill-btn"
      style={{ marginLeft: 8 }}
      onClick={() => {
        setFeedback(null);
        setInput("");
        setWaitingForNext(false);
        nextQ();
      }}
    >
      Next
    </button>
  )}
</div>

      {/* Score Display */}
      <div style={{ fontWeight: 700, fontSize: "1.15em", margin: "14px 0 6px 0" }}>
        Score: {correct} / {total}
      </div>
      {feedback && <p className="math-practice-feedback">{feedback}</p>}

      <div style={{ marginTop: 22 }}>
        <TrigPracticeGraph mode={func} highlightX={DEG_TO_RAD(angle)} width={280} height={110} />
      </div>

      {/* Worksheet buttons */}
      <div className="worksheet-label">
    Printable worksheet:
  </div>
  <div className="worksheet-btn-row">
    {[10, 25, 50].map(n => (
      <button
        key={n}
        className="math-pill-btn"
        onClick={() => {
          setWorksheet(generateWorksheetQuestions(n));
          setTimeout(() => window.print(), 60);
        }}
      >
        {n} Questions
      </button>
        ))}
      </div>
    </div>
  );
}
